import { useMemo } from 'react';
import { getLargestCidrForGap } from '../../apps/cidr-planner/splitPlanner';
import type { AddressRange, IpCountMode } from '../../apps/cidr-planner/types';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';
import { CidrSheet } from './CidrSheet';

type GapActionDialogProps = {
  gap: AddressRange;
  ipCountMode: IpCountMode;
  lang: DisplayLang;
  onClose: () => void;
  onAddOne: () => void;
  onSplit: () => void;
};

const TEXT = {
  ja: {
    title: '空き領域',
    hint: '1 つの CIDR を追加するか、等分に分割して追加できます。',
    largestCidr: (cidr: string) => `この空きに入る最大 CIDR: ${cidr}`,
    noLargestCidr: 'この空きには追加可能なサブネットがありません',
    awsNoLargest: 'AWS VPC では /28 より細かい領域のため追加できません',
    addOne: '1 つ追加',
    split: '等分分割',
    close: '閉じる',
  },
  en: {
    title: 'Free space',
    hint: 'Add a single CIDR or split the space into equal blocks.',
    largestCidr: (cidr: string) => `Largest CIDR for this space: ${cidr}`,
    noLargestCidr: 'No subnet can be added to this space',
    awsNoLargest: 'AWS VPC cannot use spaces smaller than /28',
    addOne: 'Add one',
    split: 'Split equally',
    close: 'Close',
  },
} as const;

export function GapActionDialog({
  gap,
  ipCountMode,
  lang,
  onClose,
  onAddOne,
  onSplit,
}: GapActionDialogProps) {
  const t = TEXT[lang];
  const largestCidr = useMemo(
    () => getLargestCidrForGap(gap, ipCountMode),
    [gap, ipCountMode],
  );

  return (
    <CidrSheet title={t.title} closeLabel={t.close} onClose={onClose}>
      {largestCidr ? (
        <p className="cidr-sheet__hint cidr-sheet__hint--strong">{t.largestCidr(largestCidr)}</p>
      ) : (
        <p className="cidr-sheet__hint">
          {ipCountMode === 'aws' ? t.awsNoLargest : t.noLargestCidr}
        </p>
      )}
      <p className="cidr-sheet__hint">{t.hint}</p>
      <button
        type="button"
        className="cidr-sheet__btn cidr-sheet__btn--primary cidr-sheet__btn--full"
        onClick={onAddOne}
      >
        {t.addOne}
      </button>
      <button
        type="button"
        className="cidr-sheet__btn cidr-sheet__btn--full"
        onClick={onSplit}
      >
        {t.split}
      </button>
    </CidrSheet>
  );
}
